import $ from 'jquery';
import Swal from 'sweetalert2';
import { getAxiosClient } from '~src/api';
import { getState, modifyState } from '~src/state';
import { isLoggedUserSuperUser } from '~src/user';
import { EDITOR_MODE_ORG_TEMPLATE_MANAGER } from '~config/editorConstants';
import { calculateFormatDpi } from '~loader/loader-utils/calculateFormat';
import { parseOptionalPositiveNumber } from '~loader/loader-utils/publisherImportFormatSuggestions';
import {
  getCanvasJsonBackgroundRenderedSize,
  scaleCanvasJsonForDpiChange,
} from '~core/templates';

const DEFAULT_DIMENSION_DPI = 300;
const DIMENSION_EQUAL_TOLERANCE_PX = 1;
const DIMENSION_MATCH_TOLERANCE_RATIO = 0.03;
const TEMPLATE_DPI_MIN = 72;
const TEMPLATE_DPI_MAX = 300;
const TEMPLATE_DPI_PRESETS = [72, 150, 300];
const TEMPLATE_MULTIPLIER_MIN = 1;
const TEMPLATE_MULTIPLIER_MAX = 2;
const TEMPLATE_MULTIPLIER_STEP = 0.1;

const isPositiveFiniteNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) && number > 0;
};

const validateTemplateImportSettings = ({ dpi, multiplier }) => {
  const numericDpi = Number(dpi);
  const numericMultiplier = Number(multiplier);

  if (
    !Number.isInteger(numericDpi) ||
    numericDpi < TEMPLATE_DPI_MIN ||
    numericDpi > TEMPLATE_DPI_MAX
  ) {
    return `DPI deve essere un numero intero compreso tra ${TEMPLATE_DPI_MIN} e ${TEMPLATE_DPI_MAX}`;
  }

  if (
    !Number.isFinite(numericMultiplier) ||
    numericMultiplier < TEMPLATE_MULTIPLIER_MIN ||
    numericMultiplier > TEMPLATE_MULTIPLIER_MAX
  ) {
    return `Multiplier deve essere compreso tra ${TEMPLATE_MULTIPLIER_MIN} e ${TEMPLATE_MULTIPLIER_MAX}`;
  }

  return null;
};

const stripImportSuffix = (name = 'Formato') =>
  String(name).replace(
    /\s+import(?:\s+\d+(?:\.\d+)?dpi)?(?:\s+x\d+(?:\.\d+)?)?$/i,
    ''
  );

const resizeDimensionsForDpi = (
  originalWidth,
  originalHeight,
  sourceDpi,
  targetDpi
) => {
  const dpiProportion = sourceDpi / targetDpi;

  return {
    width: Math.round(originalWidth / dpiProportion),
    height: Math.round(originalHeight / dpiProportion),
  };
};

const dimensionsMatch = (dimensions, expectedDimensions) => {
  if (!dimensions || !expectedDimensions) return false;

  const width = Number(dimensions.width);
  const height = Number(dimensions.height);
  const expectedWidth = Number(expectedDimensions.width);
  const expectedHeight = Number(expectedDimensions.height);

  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    !Number.isFinite(expectedWidth) ||
    !Number.isFinite(expectedHeight) ||
    expectedWidth <= 0 ||
    expectedHeight <= 0
  ) {
    return false;
  }

  return (
    Math.abs(width - expectedWidth) / expectedWidth <=
      DIMENSION_MATCH_TOLERANCE_RATIO &&
    Math.abs(height - expectedHeight) / expectedHeight <=
      DIMENSION_MATCH_TOLERANCE_RATIO
  );
};

const dimensionsAreEqual = (dimensions, expectedDimensions) => {
  if (!dimensions || !expectedDimensions) return false;

  const width = Number(dimensions.width);
  const height = Number(dimensions.height);
  const expectedWidth = Number(expectedDimensions.width);
  const expectedHeight = Number(expectedDimensions.height);

  return (
    Number.isFinite(width) &&
    Number.isFinite(height) &&
    Number.isFinite(expectedWidth) &&
    Number.isFinite(expectedHeight) &&
    Math.abs(width - expectedWidth) <= DIMENSION_EQUAL_TOLERANCE_PX &&
    Math.abs(height - expectedHeight) <= DIMENSION_EQUAL_TOLERANCE_PX
  );
};

const getFormatFromTemplate = (template) =>
  template?.editor_project_formats_pointer;

const getDefaultFormatFromTemplate = (template) =>
  template?.editor_project_formats_default_pointer;

const getLinkedDefaultFormat = (format, formatDefault) =>
  format?.editor_project_formats_default_pointer || formatDefault;

const getFormatWithDimensions = (...formats) =>
  formats.find((format) => format?.width && format?.height);

const formatHasOwnDimensions = (format) =>
  isPositiveFiniteNumber(format?.width) &&
  isPositiveFiniteNumber(format?.height);

const getBaseFormatDimensions = (template) => {
  const format = getFormatFromTemplate(template);
  const formatDefault = getDefaultFormatFromTemplate(template);
  const linkedDefaultFormat = getLinkedDefaultFormat(format, formatDefault);
  const baseFormat = getFormatWithDimensions(
    format,
    linkedDefaultFormat,
    formatDefault
  );
  const width = Number(baseFormat?.width);
  const height = Number(baseFormat?.height);

  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    throw new Error('Dimensioni del formato non disponibili');
  }

  return { width, height };
};

const getEffectiveDimensionsForDpi = (template, dpi) => {
  const { width, height } = getBaseFormatDimensions(template);

  return resizeDimensionsForDpi(width, height, DEFAULT_DIMENSION_DPI, dpi);
};

const getProjectTypeFromTemplate = (template) =>
  template?.editor_project_types_pointer ||
  template?.editor_project_types_default_pointer ||
  getState('selected.tipo');

const getCurrentDpi = (template) => {
  const format = getFormatFromTemplate(template);
  const formatDefault = getDefaultFormatFromTemplate(template);
  return calculateFormatDpi(
    {
      editor_project_formats_pointer: format,
      editor_project_formats_default_pointer: formatDefault,
    },
    DEFAULT_DIMENSION_DPI
  );
};

const getCurrentMultiplier = (template) => {
  const format = getFormatFromTemplate(template);
  const projectType = getProjectTypeFromTemplate(template);
  return format?.multiplier || projectType?.multiplier || 1;
};

const getPointerId = (obj) => obj?.objectId || obj?.id;

const getKnownDpisForTemplate = (template, currentDpi) => {
  const format = getFormatFromTemplate(template);
  const formatDefault = getDefaultFormatFromTemplate(template);
  const linkedDefaultFormat = getLinkedDefaultFormat(format, formatDefault);
  const candidateDpis = [DEFAULT_DIMENSION_DPI, currentDpi];

  if (formatHasOwnDimensions(format)) {
    candidateDpis.push(format?.dpi);
  }
  candidateDpis.push(linkedDefaultFormat?.dpi, formatDefault?.dpi);

  return [...new Set(candidateDpis.map(Number).filter(isPositiveFiniteNumber))];
};

const getKnownSourceDimensionsForTemplate = (template, currentDpi) =>
  getKnownDpisForTemplate(template, currentDpi).map((dpi) =>
    getEffectiveDimensionsForDpi(template, dpi)
  );

const fetchOrgTemplateForImportSettings = async (templateId) => {
  const httpClient = await getAxiosClient(true);
  const pointersToInclude = [
    'editor_project_formats_default_pointer',
    'editor_project_formats_pointer',
    'editor_project_types_default_pointer',
    'editor_project_types_pointer',
    'editor_templates_categories_default_pointer',
    'editor_templates_categories_pointer',
  ];

  const response = await httpClient.get(
    `${process.env.PARSE_API_URL}/editor_templates`,
    {
      params: {
        filterEqual_objectId: templateId,
        include: pointersToInclude.join(','),
      },
    }
  );
  const template = response.data.data[0];

  if (!template) {
    throw new Error('Template not found');
  }

  return template;
};

const fetchOrgTemplatePagesForImportSettings = async (templateId) => {
  const httpClient = await getAxiosClient(true);
  const response = await httpClient.get(
    `${process.env.PARSE_API_URL}/editor_templates_pages`,
    {
      params: {
        filterEqual_editor_templates_pointer: templateId,
        include:
          'editor_project_formats_default_pointer,editor_project_formats_pointer',
      },
    }
  );

  return (response.data.data || []).sort(
    (a, b) => Number(a.order || 0) - Number(b.order || 0)
  );
};

const buildFormatOverridePayload = (template, { dpi, multiplier }) => {
  const format = getFormatFromTemplate(template);
  const formatDefault = getDefaultFormatFromTemplate(template);
  const projectType = getProjectTypeFromTemplate(template);
  const baseFormat = format?.objectId ? format : formatDefault;
  const linkedDefaultFormat = getLinkedDefaultFormat(format, formatDefault);

  if (!baseFormat?.objectId && !formatDefault?.objectId) {
    throw new Error('Formato del modello non disponibile');
  }

  const { width, height } = getBaseFormatDimensions(template);
  const orientation =
    baseFormat.orientation ||
    linkedDefaultFormat?.orientation ||
    (width >= height ? 'h' : 'v');
  const baseName = stripImportSuffix(
    baseFormat.name || linkedDefaultFormat?.name || 'Formato'
  );
  const payload = {
    name: `${baseName} import ${dpi}dpi x${multiplier}`,
    isCustom: false,
    unit: 'px',
    width,
    height,
    dpi,
    multiplier,
    orientation,
  };

  const defaultFormatId = getPointerId(linkedDefaultFormat);
  if (defaultFormatId) {
    payload.editor_project_formats_default_pointer = defaultFormatId;
  }

  if (projectType?.objectId) {
    if (projectType.isGlobal === true) {
      payload.editor_project_types_default_pointer = projectType.objectId;
    } else {
      payload.editor_project_types_pointer = projectType.objectId;
    }
  }

  return {
    payload,
    linkedDefaultFormat,
  };
};

const createFormatOverride = async (template, { dpi, multiplier }) => {
  const httpClient = await getAxiosClient();
  const { payload, linkedDefaultFormat } = buildFormatOverridePayload(
    template,
    {
      dpi,
      multiplier,
    }
  );
  const response = await httpClient.post(
    `${process.env.PARSE_API_URL}/editor_project_formats`,
    { data: payload }
  );
  const createdFormat = response.data.data;

  return { createdFormat, linkedDefaultFormat };
};

const getTargetFormatForSettings = async (
  template,
  { dpi, multiplier },
  settingsChanged
) => {
  const format = getFormatFromTemplate(template);
  const formatDefault = getDefaultFormatFromTemplate(template);
  const linkedDefaultFormat = getLinkedDefaultFormat(format, formatDefault);

  if (!settingsChanged) {
    return {
      targetFormat: format?.objectId ? format : null,
      linkedDefaultFormat,
    };
  }

  const { createdFormat } = await createFormatOverride(template, {
    dpi,
    multiplier,
  });

  return {
    targetFormat: createdFormat,
    linkedDefaultFormat,
  };
};

const buildFormatPointerData = (targetFormat, linkedDefaultFormat) => {
  const data = {};

  if (targetFormat?.objectId) {
    data.editor_project_formats_pointer = targetFormat.objectId;
  }

  if (linkedDefaultFormat?.objectId) {
    data.editor_project_formats_default_pointer = linkedDefaultFormat.objectId;
  }

  return data;
};

const buildOriginalPageData = (page) => ({
  canvasJson: page.canvasJson || '',
  hash: page.hash || null,
  editor_project_formats_pointer:
    getPointerId(page.editor_project_formats_pointer) || null,
  editor_project_formats_default_pointer:
    getPointerId(page.editor_project_formats_default_pointer) || null,
});

const getInferredPageSourceDimensions = (
  page,
  nominalSourceDimensions,
  targetDimensions,
  knownSourceDimensions
) => {
  if (!page.canvasJson) {
    return nominalSourceDimensions;
  }

  const backgroundDimensions = getCanvasJsonBackgroundRenderedSize(
    page.canvasJson
  );

  if (
    backgroundDimensions &&
    dimensionsAreEqual(backgroundDimensions, targetDimensions)
  ) {
    return targetDimensions;
  }

  if (
    backgroundDimensions &&
    !dimensionsAreEqual(backgroundDimensions, targetDimensions)
  ) {
    const matchingKnownDimensions = knownSourceDimensions.find(
      (sourceDimensions) =>
        !dimensionsAreEqual(sourceDimensions, targetDimensions) &&
        dimensionsMatch(backgroundDimensions, sourceDimensions)
    );

    if (matchingKnownDimensions) {
      return backgroundDimensions;
    }
  }

  return nominalSourceDimensions;
};

const buildPageMigrationPlan = (template, pages, targetDpi) => {
  const currentDpi = getCurrentDpi(template);
  const nominalSourceDimensions = getEffectiveDimensionsForDpi(
    template,
    currentDpi
  );
  const targetDimensions = getEffectiveDimensionsForDpi(template, targetDpi);
  const knownSourceDimensions = getKnownSourceDimensionsForTemplate(
    template,
    currentDpi
  );

  return pages.map((page) => {
    if (!page.canvasJson) {
      return {
        page,
        canvasJson: page.canvasJson,
        migrated: false,
        sourceDimensions: nominalSourceDimensions,
        targetDimensions,
      };
    }

    const sourceDimensions = getInferredPageSourceDimensions(
      page,
      nominalSourceDimensions,
      targetDimensions,
      knownSourceDimensions
    );
    const shouldMigrate = !dimensionsAreEqual(
      sourceDimensions,
      targetDimensions
    );
    const canvasJson = shouldMigrate
      ? scaleCanvasJsonForDpiChange(
          page.canvasJson,
          sourceDimensions,
          targetDimensions
        )
      : page.canvasJson;

    return {
      page,
      canvasJson,
      migrated: shouldMigrate,
      sourceDimensions,
      targetDimensions,
    };
  });
};

const getMigratedPagesCount = (pageMigrationPlan) =>
  pageMigrationPlan.filter((migration) => migration.migrated).length;

const getFirstMigratedPage = (pageMigrationPlan) =>
  pageMigrationPlan.find((migration) => migration.migrated);

const formatDimensionsLabel = (dimensions) =>
  `${Math.round(Number(dimensions.width))}x${Math.round(
    Number(dimensions.height)
  )}`;

const buildRepairNoticeHtml = (repairPlan) => {
  const migratedPagesCount = getMigratedPagesCount(repairPlan);
  if (migratedPagesCount === 0) return '';

  const firstMigration = getFirstMigratedPage(repairPlan);
  const sourceLabel = formatDimensionsLabel(firstMigration.sourceDimensions);
  const targetLabel = formatDimensionsLabel(firstMigration.targetDimensions);
  const pagesLabel =
    migratedPagesCount === 1
      ? '1 pagina sembra salvata'
      : `${migratedPagesCount} pagine sembrano salvate`;

  return `
    <div class="template-import-repair-notice">
      <div class="template-import-repair-title">
        Scala canvas non coerente
      </div>
      <div class="template-import-repair-copy">
        ${pagesLabel} a ${sourceLabel}, mentre il formato corrente richiede
        ${targetLabel}. Usa "Ripara canvas" per riscalare il JSON senza
        cambiare DPI o multiplier.
      </div>
    </div>
  `;
};

const rollbackUpdatedPages = async (httpClient, updatedPages) => {
  await Promise.allSettled(
    updatedPages.map(({ page }) =>
      httpClient.put(
        `${process.env.PARSE_API_URL}/editor_templates_pages/${page.objectId}`,
        { data: buildOriginalPageData(page) }
      )
    )
  );
};

const updateTemplateAndPages = async (
  template,
  pageMigrationPlan,
  targetFormat,
  linkedDefaultFormat,
  invalidatePreviewHash = false
) => {
  const httpClient = await getAxiosClient();
  const formatPointerData = buildFormatPointerData(
    targetFormat,
    linkedDefaultFormat
  );
  const updatedPages = [];

  try {
    for (const migration of pageMigrationPlan) {
      const { page, canvasJson } = migration;
      if (!page.objectId) continue;
      const pageData = { ...formatPointerData };

      if (typeof canvasJson !== 'undefined') {
        pageData.canvasJson = canvasJson;
      }
      if (invalidatePreviewHash) {
        pageData.hash = null;
      }

      await httpClient.put(
        `${process.env.PARSE_API_URL}/editor_templates_pages/${page.objectId}`,
        {
          data: pageData,
        }
      );
      updatedPages.push(migration);
    }

    const formatName =
      targetFormat?.name ||
      linkedDefaultFormat?.name ||
      template.metadata?.formatName;

    await httpClient.put(
      `${process.env.PARSE_API_URL}/editor_templates/${template.objectId}`,
      {
        data: {
          ...formatPointerData,
          metadata: {
            ...(template.metadata || {}),
            ...(formatName ? { formatName } : {}),
          },
        },
      }
    );
  } catch (error) {
    await rollbackUpdatedPages(httpClient, updatedPages);
    throw error;
  }
};

const updateSelectedFormatState = (createdFormat, linkedDefaultFormat) => {
  if (!createdFormat?.objectId) {
    return;
  }

  modifyState('selected.formato', {
    ...createdFormat,
    editor_project_formats_default_pointer: linkedDefaultFormat,
  });
  if (linkedDefaultFormat?.objectId) {
    modifyState('selected.formatoDefault', linkedDefaultFormat);
  }
  modifyState('selected.template.editor_project_formats_pointer', {
    ...createdFormat,
    editor_project_formats_default_pointer: linkedDefaultFormat,
  });
  if (linkedDefaultFormat?.objectId) {
    modifyState(
      'selected.template.editor_project_formats_default_pointer',
      linkedDefaultFormat
    );
  }
};

const getTemplateIdFromEvent = (eventOrTemplateId) => {
  if (typeof eventOrTemplateId === 'string') return eventOrTemplateId;
  const currentTarget = eventOrTemplateId?.currentTarget;
  if (!currentTarget) return window.editorLoader.templateId;

  return (
    $(currentTarget).attr('data-giotto-template-import-settings') ||
    window.editorLoader.templateId
  );
};

export const updateTemplateImportSettingsVisibility = async () => {
  const isSuperUser = await isLoggedUserSuperUser();
  const isOrgTemplateManager =
    getState('editorMode') === EDITOR_MODE_ORG_TEMPLATE_MANAGER;

  $('#editor-menu-template-import-settings').toggleClass(
    'd-none',
    !(isSuperUser && isOrgTemplateManager)
  );
};

export const onTemplateImportSettingsClick = async (eventOrTemplateId) => {
  eventOrTemplateId?.preventDefault?.();
  eventOrTemplateId?.stopPropagation?.();

  const templateId = getTemplateIdFromEvent(eventOrTemplateId);
  if (!templateId) return false;

  let template;
  try {
    template = await fetchOrgTemplateForImportSettings(templateId);
  } catch (error) {
    console.error('Cannot fetch template import settings', error);
    await Swal.fire({
      icon: 'error',
      title: 'Modello non disponibile',
      text: 'Non è stato possibile caricare le impostazioni del modello.',
      confirmButtonColor: '#278e8e',
    });
    return false;
  }

  const currentDpi = getCurrentDpi(template);
  const currentMultiplier = getCurrentMultiplier(template);

  let templatePages;
  try {
    templatePages = await fetchOrgTemplatePagesForImportSettings(
      template.objectId
    );
  } catch (error) {
    console.error('Cannot fetch template pages for import settings', error);
    await Swal.fire({
      icon: 'error',
      title: 'Pagine non disponibili',
      text: 'Non è stato possibile caricare le pagine del modello.',
      confirmButtonColor: '#278e8e',
    });
    return false;
  }

  const currentRepairPlan = buildPageMigrationPlan(
    template,
    templatePages,
    currentDpi
  );
  const repairablePagesCount = getMigratedPagesCount(currentRepairPlan);
  const repairNoticeHtml = buildRepairNoticeHtml(currentRepairPlan);

  const result = await Swal.fire({
    title: 'Impostazioni modello',
    html: `
      <div class="template-import-settings">
        <label>
          <span>DPI</span>
          <input
            type="number"
            min="${TEMPLATE_DPI_MIN}"
            max="${TEMPLATE_DPI_MAX}"
            step="1"
            list="template-import-settings-dpi-presets"
            class="template-import-settings-dpi"
            value="${currentDpi}"
          />
          <datalist id="template-import-settings-dpi-presets">
            ${TEMPLATE_DPI_PRESETS.map(
              (dpi) => `<option value="${dpi}"></option>`
            ).join('')}
          </datalist>
          <small class="import-setting-hint">
            Più DPI significa canvas più grande in pixel, quindi più memoria e
            rendering più lento nel browser. DPI più bassi alleggeriscono
            l'editor ma riducono la risoluzione. Valori consigliati:
            ${TEMPLATE_DPI_PRESETS.join(', ')}.
          </small>
        </label>
        <label>
          <span>Multiplier</span>
          <input
            type="number"
            min="${TEMPLATE_MULTIPLIER_MIN}"
            max="${TEMPLATE_MULTIPLIER_MAX}"
            step="${TEMPLATE_MULTIPLIER_STEP}"
            class="template-import-settings-multiplier"
            value="${currentMultiplier}"
          />
          <small class="import-setting-hint">
            Il multiplier aumenta la risoluzione di export senza cambiare il
            layout del modello.
          </small>
        </label>
      </div>
      ${repairNoticeHtml}
    `,
    showCancelButton: true,
    showDenyButton: repairablePagesCount > 0,
    confirmButtonText: 'Salva impostazioni',
    denyButtonText: 'Ripara canvas',
    cancelButtonText: 'Annulla',
    confirmButtonColor: '#278e8e',
    denyButtonColor: '#8a5a00',
    focusConfirm: false,
    preConfirm: () => {
      const dpi = parseOptionalPositiveNumber(
        document.querySelector('.template-import-settings-dpi')?.value
      );
      const multiplier = parseOptionalPositiveNumber(
        document.querySelector('.template-import-settings-multiplier')?.value
      );
      const validationMessage = validateTemplateImportSettings({
        dpi,
        multiplier,
      });

      if (validationMessage) {
        Swal.showValidationMessage(validationMessage);
        return false;
      }

      return { dpi, multiplier };
    },
  });

  if (result.isDenied) {
    const validationMessage = validateTemplateImportSettings({
      dpi: currentDpi,
      multiplier: currentMultiplier,
    });

    if (validationMessage) {
      await Swal.fire({
        icon: 'warning',
        title: 'Impostazioni fuori limite',
        text: `${validationMessage}. Correggi i valori prima di riparare il canvas.`,
        confirmButtonColor: '#278e8e',
      });
      return false;
    }

    Swal.fire({
      title: 'Riparazione canvas in corso',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading(),
    });

    try {
      const repairPlan = currentRepairPlan.filter(
        (migration) => migration.migrated
      );
      const {
        targetFormat,
        linkedDefaultFormat,
      } = await getTargetFormatForSettings(
        template,
        { dpi: currentDpi, multiplier: currentMultiplier },
        false
      );

      await updateTemplateAndPages(
        template,
        repairPlan,
        targetFormat,
        linkedDefaultFormat,
        true
      );

      const isOpenTemplate =
        window.editorLoader.templateId === template.objectId;
      if (isOpenTemplate) {
        updateSelectedFormatState(targetFormat, linkedDefaultFormat);
      }

      await Swal.fire({
        icon: 'success',
        title: 'Canvas riparato',
        text:
          repairablePagesCount === 1
            ? 'La pagina del modello è stata riscalata al DPI corrente.'
            : `${repairablePagesCount} pagine del modello sono state riscalate al DPI corrente.`,
        confirmButtonText: isOpenTemplate ? 'Ricarica modello' : 'Ok',
        confirmButtonColor: '#278e8e',
      });
      if (isOpenTemplate) {
        window.location.reload();
      }
      return true;
    } catch (error) {
      console.error('Cannot repair template canvas scale', error);
      await Swal.fire({
        icon: 'error',
        title: 'Riparazione non riuscita',
        text: 'Non è stato possibile riscalare le pagine del modello.',
        confirmButtonColor: '#278e8e',
      });
      return false;
    }
  }

  if (!result.isConfirmed) return false;

  Swal.fire({
    title: 'Salvataggio in corso',
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    willOpen: () => Swal.showLoading(),
  });

  try {
    const settingsChanged =
      Number(result.value.dpi) !== Number(currentDpi) ||
      Number(result.value.multiplier) !== Number(currentMultiplier);
    const pageMigrationPlan = buildPageMigrationPlan(
      template,
      templatePages,
      result.value.dpi
    );
    const migratedPagesCount = getMigratedPagesCount(pageMigrationPlan);
    const {
      targetFormat,
      linkedDefaultFormat,
    } = await getTargetFormatForSettings(
      template,
      result.value,
      settingsChanged
    );

    await updateTemplateAndPages(
      template,
      pageMigrationPlan,
      targetFormat,
      linkedDefaultFormat,
      settingsChanged || migratedPagesCount > 0
    );

    const isOpenTemplate = window.editorLoader.templateId === template.objectId;
    if (isOpenTemplate) {
      updateSelectedFormatState(targetFormat, linkedDefaultFormat);
    }

    await Swal.fire({
      icon: 'success',
      title: 'Impostazioni salvate',
      text:
        migratedPagesCount > 0
          ? 'DPI, multiplier e proporzioni del modello sono stati aggiornati.'
          : isOpenTemplate
          ? 'Il modello viene ricaricato per applicare DPI e multiplier.'
          : 'DPI e multiplier del modello sono stati aggiornati.',
      confirmButtonText: isOpenTemplate ? 'Ricarica modello' : 'Ok',
      confirmButtonColor: '#278e8e',
    });
    if (isOpenTemplate) {
      window.location.reload();
    }
    return true;
  } catch (error) {
    console.error('Cannot update template import settings', error);
    await Swal.fire({
      icon: 'error',
      title: 'Salvataggio non riuscito',
      text: 'Non è stato possibile aggiornare DPI e multiplier.',
      confirmButtonColor: '#278e8e',
    });
    return false;
  }
};

export const templateImportSettingsTestables = {
  buildPageMigrationPlan,
  buildFormatOverridePayload,
  getBaseFormatDimensions,
  getCurrentDpi,
  validateTemplateImportSettings,
};
